import { useEffect, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import * as Y from 'yjs'
import { yDocToProsemirrorJSON } from 'y-prosemirror'
import { api } from './api'
import { baseEditorExtensions } from './editorExtensions'
import { Modal } from './Modal'

type Version = {
  id: string
  reason: string
  createdAt: string
}

function decodeState(value: string): Uint8Array {
  const binary = atob(value)
  return Uint8Array.from(binary, (character) => character.charCodeAt(0))
}

function VersionPreview({ state }: { state: string }) {
  const document = new Y.Doc()
  Y.applyUpdate(document, decodeState(state))
  const content = yDocToProsemirrorJSON(document, 'default')
  const editor = useEditor({
    extensions: baseEditorExtensions,
    content,
    editable: false,
  }, [state])

  return <EditorContent editor={editor} className="version-preview" />
}

const reasonLabel: Record<string, string> = {
  auto: '自动版本',
  manual: '手动版本',
  before_restore: '恢复前',
  backup: '备份时',
}

export function VersionHistory({ onClose }: { onClose: () => void }) {
  const [versions, setVersions] = useState<Version[]>([])
  const [selected, setSelected] = useState<{ id: string; state: string } | null>(null)
  const [loading, setLoading] = useState(true)
  const [restoring, setRestoring] = useState(false)

  useEffect(() => {
    void api<{ versions: Version[] }>('/api/versions')
      .then((result) => setVersions(result.versions))
      .finally(() => setLoading(false))
  }, [])

  async function openVersion(version: Version) {
    const result = await api<{ id: string; state: string }>(`/api/versions/${version.id}`)
    setSelected(result)
  }

  async function restore() {
    if (!selected || !window.confirm('恢复这个版本？当前内容会先自动保存为“恢复前”版本。')) return
    setRestoring(true)
    try {
      await api(`/api/versions/${selected.id}/restore`, {
        method: 'POST',
        body: JSON.stringify({}),
      })
      onClose()
    } finally {
      setRestoring(false)
    }
  }

  return (
    <Modal title="版本历史" onClose={onClose}>
      {selected ? (
        <>
          <div className="modal-actions">
            <button onClick={() => setSelected(null)}>返回列表</button>
            <button className="primary-button compact" onClick={restore} disabled={restoring}>
              {restoring ? '正在恢复…' : '恢复此版本'}
            </button>
          </div>
          <VersionPreview state={selected.state} />
        </>
      ) : (
        <div className="version-list">
          {loading && <p className="muted">正在载入…</p>}
          {!loading && versions.length === 0 && (
            <p className="empty-state">还没有历史版本。开始编辑后会自动建立。</p>
          )}
          {versions.map((version) => (
            <button key={version.id} className="version-row" onClick={() => void openVersion(version)}>
              <span>{new Date(version.createdAt).toLocaleString('zh-CN')}</span>
              <small>{reasonLabel[version.reason] ?? version.reason}</small>
            </button>
          ))}
        </div>
      )}
    </Modal>
  )
}
